/**
 * Feature Gate Component
 * Controls access to features based on subscription status
 */

import React from 'react';
import {
  Box,
  VStack,
  Text,
  Button,
  ButtonText,
  Pressable,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useSubscriptionStore, useHasProAccess, useIsInTrial } from '../store/subscriptionStore';
import { SubscriptionPlan, FREE_LIMITS, PRO_LIMITS, FeatureLimits } from '../types';
import { FS_SM, FS_MD, FS_LG, FS_2XL } from '../theme';
import { useDisplayTheme } from '../hooks/useDisplayTheme';

type FeatureKey = keyof FeatureLimits;

interface FeatureGateProps {
  /** The feature being gated */
  feature: FeatureKey;
  /** Content to render when user has access */
  children: React.ReactNode;
  /** Optional fallback when access is denied (defaults to upgrade prompt) */
  fallback?: React.ReactNode;
  /** If true, shows children but overlays upgrade prompt */
  softGate?: boolean;
}

/**
 * Get feature limits based on subscription plan
 */
export function getFeatureLimits(plan: SubscriptionPlan): FeatureLimits {
  return plan === 'free' ? FREE_LIMITS : PRO_LIMITS;
}

/**
 * Check if a feature is available for a plan
 */
export function isFeatureAvailable(plan: SubscriptionPlan, feature: FeatureKey): boolean {
  const limits = getFeatureLimits(plan);
  const value = limits[feature];
  if (typeof value === 'boolean') return value;
  return value > 0;
}

/** Translate feature name using literal keys (required by literal-i18n-keys rule) */
function translateFeatureName(t: TFunction, feature: FeatureKey): string {
  switch (feature) {
    case 'pronunciationAssessments': return t('featureGate.features.pronunciationAssessments');
    case 'aiConversations': return t('featureGate.features.aiConversations');
    case 'lessons': return t('featureGate.features.lessons');
    case 'detailedFeedback': return t('featureGate.features.detailedFeedback');
    case 'audioLessons': return t('featureGate.features.audioLessons');
  }
}

/**
 * FeatureGate Component
 * Wraps content that requires specific subscription access
 */
export function FeatureGate({ feature, children, fallback, softGate = false }: FeatureGateProps) {
  const navigation = useNavigation();
  const subscription = useSubscriptionStore((state) => state.subscription);
  const hasProAccess = useHasProAccess();
  const isInTrial = useIsInTrial();
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const plan = subscription?.plan || 'free';
  const hasAccess = hasProAccess || isInTrial || isFeatureAvailable(plan, feature);
  const featureName = translateFeatureName(t, feature);

  const handleUpgrade = () => {
    (navigation.navigate as (screen: string, params: Record<string, string>) => void)('Paywall', { feature: featureName });
  };

  // User has access - render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // Custom fallback provided
  if (fallback) {
    return <>{fallback}</>;
  }

  // Soft gate - show content with overlay
  if (softGate) {
    return (
      <Box position="relative">
        <Box opacity={0.3} pointerEvents="none">
          {children}
        </Box>
        <Pressable
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          justifyContent="center"
          alignItems="center"
          bg="rgba(0,0,0,0.5)"
          onPress={handleUpgrade}
        >
          <Box style={{ backgroundColor: theme.card }} p="$4" rounded="$xl" mx="$4" maxWidth="$80">
            <VStack space="sm" alignItems="center">
              <Text allowFontScaling={false} style={{ fontSize: FS_2XL * fontScale, ...ffs }}>
                🔒
              </Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.text, fontWeight: '600', textAlign: 'center', ...ffs }}>
                {featureName}
              </Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}>
                {t('featureGate.upgradeToUnlock')}
              </Text>
              <Button size="sm" style={{ backgroundColor: theme.primary }} onPress={handleUpgrade} mt="$2">
                <ButtonText allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: 'white', ...ffs }}>{t('common.upgrade')}</ButtonText>
              </Button>
            </VStack>
          </Box>
        </Pressable>
      </Box>
    );
  }

  // Default fallback - upgrade prompt card
  return (
    <Box style={{ backgroundColor: theme.card }} p="$5" rounded="$xl" hardShadow="2" mx="$4" my="$4">
      <VStack space="md" alignItems="center">
        <Box
          w="$16"
          h="$16"
          rounded="$full"
          style={{ backgroundColor: theme.primary }}
          justifyContent="center"
          alignItems="center"
        >
          <Text allowFontScaling={false} style={{ fontSize: FS_2XL * fontScale, color: 'white', ...ffs }}>
            🔒
          </Text>
        </Box>
        <Text allowFontScaling={false} accessibilityRole="header" style={{ fontSize: FS_LG * fontScale, fontWeight: 'bold', color: theme.text, textAlign: 'center', ...ffs }}>
          {featureName}
        </Text>
        <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}>
          {t('featureGate.featureRequiresPro')}
        </Text>
        <Button size="md" style={{ backgroundColor: theme.primary }} onPress={handleUpgrade}>
          <ButtonText allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', ...ffs }}>{t('featureGate.upgradeToPro')}</ButtonText>
        </Button>
      </VStack>
    </Box>
  );
}

/**
 * Hook to check feature access
 */
export function useFeatureAccess(feature: FeatureKey): {
  hasAccess: boolean;
  limit: number | boolean;
  plan: SubscriptionPlan;
} {
  const subscription = useSubscriptionStore((state) => state.subscription);
  const hasProAccess = useHasProAccess();
  const isInTrial = useIsInTrial();

  const plan = subscription?.plan || 'free';
  const limits = getFeatureLimits(plan);
  const limit = limits[feature];
  const hasAccess = hasProAccess || isInTrial || isFeatureAvailable(plan, feature);

  return { hasAccess, limit, plan };
}

/**
 * UpgradePrompt Component
 * Standalone prompt to encourage upgrade
 */
interface UpgradePromptProps {
  title?: string;
  message?: string;
  compact?: boolean;
}

export function UpgradePrompt({
  title,
  message,
  compact = false,
}: UpgradePromptProps) {
  const navigation = useNavigation();
  const hasProAccess = useHasProAccess();
  const isInTrial = useIsInTrial();
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const resolvedTitle = title ?? t('featureGate.unlockMoreFeatures');
  const resolvedMessage = message ?? t('featureGate.unlockDescription');

  // Don't show if user already has access
  if (hasProAccess) return null;

  const handleUpgrade = () => {
    navigation.navigate('Paywall');
  };

  if (compact) {
    return (
      <Pressable onPress={handleUpgrade}>
        <Box
          style={{ backgroundColor: theme.primary }}
          p="$3"
          rounded="$lg"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', fontWeight: '600', flex: 1, ...ffs }}>
            {isInTrial ? `🎉 ${t('featureGate.trialActive')}` : `⭐ ${t('featureGate.upgradeToPro')}`}
          </Text>
          <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: 'white', ...ffs }}>
            →
          </Text>
        </Box>
      </Pressable>
    );
  }

  return (
    <Box style={{ backgroundColor: theme.card }} p="$5" rounded="$xl" hardShadow="2">
      <VStack space="md">
        <Text allowFontScaling={false} accessibilityRole="header" style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: theme.text, ...ffs }}>
          {resolvedTitle}
        </Text>
        <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.textSecondary, ...ffs }}>
          {resolvedMessage}
        </Text>
        <Button size="md" style={{ backgroundColor: theme.primary }} onPress={handleUpgrade}>
          <ButtonText allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', ...ffs }}>
            {isInTrial ? t('featureGate.viewSubscription') : t('featureGate.upgradeNow')}
          </ButtonText>
        </Button>
      </VStack>
    </Box>
  );
}

/**
 * TrialBanner Component
 * Shows trial status and days remaining
 */
export function TrialBanner() {
  const navigation = useNavigation();
  const subscription = useSubscriptionStore((state) => state.subscription);
  const isInTrial = useIsInTrial();
  const hasProAccess = useHasProAccess();
  const { t } = useTranslation();
  const { fontScale, ffs } = useDisplayTheme();

  // Don't show if not in trial or already subscribed
  if (!isInTrial || hasProAccess) return null;

  const daysLeft = subscription?.trialDaysLeft || 0;

  const handlePress = () => {
    navigation.navigate('Paywall');
  };

  return (
    <Pressable onPress={handlePress}>
      <Box
        bg={daysLeft <= 2 ? '$warning' : '$success'}
        px="$4"
        py="$3"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
      >
        <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', fontWeight: '600', ...ffs }}>
          {daysLeft <= 0
            ? `⏰ ${t('featureGate.trialExpiresToday')}`
            : `🎉 ${t('featureGate.trialDaysLeft', { count: daysLeft })}`}
        </Text>
        <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', ...ffs }} ml="$2">
          →
        </Text>
      </Box>
    </Pressable>
  );
}
